import { FormArray, FormControl, FormGroup } from '@angular/forms';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

import {
    ApiError,
    ApiResponse,
    CouponDetailInterface,
    CouponPlanScopeItemInterface,
    HttpStatusCodesEnum,
    TradingPlanSearchRequestModel,
    TradingPlanSearchResponseInterface,
    TradingPlanSearchResponseModel,
    TradingPlanService
} from '@spa/api';
import { DuplicatedValueValidatorKey } from '@spa/validators';

import { environment } from 'appEnv/environment';
import { AutocompleteControl, BaseControl } from 'appShared/classes';
import { AutocompleteOptionInterface } from 'appShared/components';
import { Pagination } from 'appShared/components';
import {
    CouponPlanScopeMaxPlans,
    CouponPlanScopePlanControl,
    CouponPlanScopeSpecificPlans,
    CouponPlanScopesFormArraySelector,
    CouponTradingPlanOptionLabel
} from 'appAssets/descriptors/panel-coupons.descriptor';

/**
 * The `SpecificPlans` half of a coupon's plan scopes: a removable list of trading-plan
 * autocompletes, each searching the plans endpoint as the operator types.
 *
 * Kept as its own `FormGroup` because `app-form-array` renders its own `<form>` element and so
 * cannot be nested inside the coupon form.
 */
export class CouponPlanScopesForm {
    private readonly tradingPlanService: TradingPlanService;

    public form: FormGroup;
    public controls: BaseControl[];
    public controlsObservable: BehaviorSubject<BaseControl[]>;

    public readonly planScopesFormArraySelector: string;

    constructor(tradingPlanService: TradingPlanService, coupon?: CouponDetailInterface) {
        this.tradingPlanService = tradingPlanService;
        this.controls = [];
        this.planScopesFormArraySelector = CouponPlanScopesFormArraySelector;
        this.controlsObservable = new BehaviorSubject([]);

        this.initForm(coupon);
    }

    /**
     * Searches trading plans by name. Returns nothing for an empty term so the panel stays closed
     * until the operator types.
     */
    private searchTradingPlans(name: string): Observable<AutocompleteOptionInterface[]> {
        if (!name) {
            return of([]);
        }

        const filter = new TradingPlanSearchRequestModel(
            Pagination.defaultPage,
            Pagination.defaultItemsPerPage,
            TradingPlanSearchResponseModel.getNameSelector(),
            true,
            null,
            null,
            name,
            null,
            null,
            null,
            environment.tenant
        );

        return this.tradingPlanService.search(filter.serialize()).pipe(
            map((response: ApiResponse<ApiError[] | TradingPlanSearchResponseInterface[]>) => {
                if (response.status !== HttpStatusCodesEnum.Ok) {
                    return [];
                }

                const plans = (response.data as TradingPlanSearchResponseInterface[]) || [];

                if (plans.length === 0) {
                    return [{ label: `${name} - no matching plan.`, value: null }];
                }

                return plans.map((plan: TradingPlanSearchResponseInterface) => ({
                    label: CouponTradingPlanOptionLabel(plan),
                    value: plan.id
                }));
            })
        );
    }

    private getFormArray(): FormArray {
        return this.form.get(this.planScopesFormArraySelector) as FormArray;
    }

    private initControl(scope?: CouponPlanScopeItemInterface): AutocompleteControl {
        const control = CouponPlanScopePlanControl(scope);

        control.asyncOptions = (name: string) => this.searchTradingPlans(name);

        return control;
    }

    initForm(coupon?: CouponDetailInterface): void {
        this.form = new FormGroup({});

        const scopes = CouponPlanScopeSpecificPlans(coupon);
        this.controls = scopes.length !== 0
            ? scopes.map((scope: CouponPlanScopeItemInterface) => this.initControl(scope))
            : [this.initControl()];

        const formArray = new FormArray([]);
        this.controls.forEach((control: BaseControl) => {
            formArray.push(new FormControl(control.value, control.validators));
        });

        this.form.addControl(this.planScopesFormArraySelector, formArray);
        this.controlsObservable.next(this.controls);
    }

    addPlan(): void {
        if (!this.canAddPlan()) {
            return;
        }

        const control = this.initControl();

        this.controls.push(control);
        this.getFormArray().push(new FormControl(control.value, control.validators));
        this.controlsObservable.next(this.controls);
    }

    canAddPlan(): boolean {
        return this.controls.length < CouponPlanScopeMaxPlans;
    }

    setDisabled(disabled: boolean): void {
        this.controls.forEach((control: BaseControl) => {
            control.disabled = disabled;
            control.removable = !disabled;
        });

        if (disabled) {
            this.getFormArray().disable();
        } else {
            this.getFormArray().enable();
        }

        this.controlsObservable.next(this.controls);
    }

    /**
     * Selected plan ids, with blank rows dropped. Uses `getRawValue` so a frozen list — the scopes
     * of an active coupon — still reports its plans back on save.
     */
    getPlanIds(): number[] {
        const values = (this.getFormArray().getRawValue() || []) as number[];

        return values.filter((planId: number) => planId != null);
    }

    validateForm(
        messagesFunction: (controlLabel?: string, controlLength?: number) => { [key: string]: string }
    ): boolean {
        const planIds = this.getFormArray().getRawValue() as number[];

        const statuses = this.controls.map((control: BaseControl, index: number) => {
            control.resetErrors();

            const planId = planIds[index];

            // A blank row is how an operator leaves the list unchanged; only duplicates are wrong.
            if (planId == null) {
                return true;
            }

            if (planIds.indexOf(planId) !== index) {
                control.setError(messagesFunction(control.placeholder)[DuplicatedValueValidatorKey]);

                return false;
            }

            return true;
        });

        return statuses.indexOf(false) < 0;
    }
}
